import { z } from "zod";

import { isPostalCodeValid } from "Forms/Utils/postalCodeUtils";
import type { CountryCodes, SaveClientRequest } from "Infrastructure/Api/Api";
import {
  getTranslation,
  Resources,
} from "Infrastructure/Translations/Resources";
import {
  isCompanyNumberValid,
  isPhoneValid,
  isTaxNumberValid,
} from "Shared/Utils/validations";

export const clientFormSchema = z
  .object({
    companyName: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    companyNumber: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isCompanyNumberValid, {
        error: () => getTranslation(Resources.Validation.InvalidCompanyNumber),
      }),
    taxNumber: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isTaxNumberValid, {
        error: () => getTranslation(Resources.Validation.InvalidTaxNumber),
      }),
    email: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .check(
        z.email({
          error: () => getTranslation(Resources.Validation.InvalidEmail),
        }),
      ),
    phone: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) })
      .refine(isPhoneValid, {
        error: () => getTranslation(Resources.Validation.InvalidPhone),
      }),
    address: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    city: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    postalCode: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
    country: z
      .string()
      .min(1, { error: () => getTranslation(Resources.Validation.Required) }),
  })
  .superRefine((data, ctx) => {
    if (data.postalCode && !isPostalCodeValid(data.postalCode, data.country)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: getTranslation(Resources.Validation.InvalidPostalCode),
        path: ["postalCode"],
      });
    }
  });

export type ClientFormData = z.infer<typeof clientFormSchema>;

export function toSaveClientRequest(
  formData: ClientFormData,
): SaveClientRequest {
  return {
    companyName: formData.companyName,
    companyNumber: formData.companyNumber,
    taxNumber: formData.taxNumber,
    email: formData.email,
    phone: formData.phone,
    address: {
      street: formData.address,
      postalCode: formData.postalCode,
      municipality: formData.city,
      countryCode: formData.country as CountryCodes,
    },
  };
}
